/**
 * Unified version display — consistent format across hub, lobby, topbar, bug report.
 *
 * Legacy format (still used by bug-report + hub changelog line):
 *   "client v1.2.3 · server v0.20.5"
 *
 * Full format (v1.9.0+, used by topbar widget via formatVersionFull):
 *   "client v1.2.3 · server v0.20.5 · bot v0.6.0 · cov 68/45%"
 * with optional test-count suffix.
 */

declare const __APP_VERSION__: string;
declare const __APP_SHA__: string;

/** Append a short SHA in parens unless it's empty or already in the version
 *  string (avoids "v0.44.0-g78eabef (78eabef)" / "78eabef (78eabef)"). */
function withSha(version: string, sha?: string): string {
  if (!sha || version.includes(sha)) return version;
  return `${version} (${sha})`;
}

/** Always-visible footer format with client + server version and short SHAs:
 *    "client v0.44.0 (78eabef) · server v1.99.13 (6f3fe72)"
 *  Server SHA shown only when the server exposes it (meta.serverSha). */
export function formatVersionGlobal(meta?: VersionMetaLike): string {
  const sha = typeof __APP_SHA__ !== 'undefined' ? __APP_SHA__ : '';
  let out = `client ${withSha(__APP_VERSION__, sha)}`;
  if (meta?.serverVersion) out += ` · server ${withSha(meta.serverVersion, meta.serverSha)}`;
  return out;
}

/** Short format — used in hub changelog and bug report body. */
export function formatVersion(serverVersion?: string): string {
  const client = `client ${__APP_VERSION__}`;
  if (!serverVersion) return client;
  return `${client} · server ${serverVersion}`;
}

/** Meta payload subset required by formatVersionFull — matches server
 *  cmd/server/meta.go metaResponse shape. All fields optional on the client
 *  side so the widget renders gracefully against partial / older-server
 *  responses. */
export interface VersionMetaLike {
  serverVersion?: string;
  serverSha?: string;        // short commit SHA, when the server exposes it via /api/meta
  botServiceVersion?: string;
  coverage?: { server?: number | null; client?: number | null } | null;
}

/** Full format — one-line unified widget for the topbar.
 *
 * Components are joined with " · ". Missing components are skipped so the
 * widget stays compact. Examples:
 *
 *   "v0.34.1 · srv v1.8.3 · bot v0.6.0 · cov c65/s45%"
 *   "v0.34.1 · srv v1.8.3"                  (no bot, no coverage)
 *   "v0.34.1"                               (disconnected / pre-meta)
 *
 * Coverage is rendered client-first because the client is what the user
 * is actively running — their own test-health signal is most relevant.
 */
export function formatVersionFull(meta?: VersionMetaLike): string {
  const parts: string[] = [__APP_VERSION__];
  if (meta?.serverVersion) parts.push(`srv ${meta.serverVersion}`);
  if (meta?.botServiceVersion) parts.push(`bot ${meta.botServiceVersion}`);
  const cov = meta?.coverage;
  if (cov && (cov.client != null || cov.server != null)) {
    const c = cov.client != null ? `c${Math.round(cov.client)}` : '';
    const s = cov.server != null ? `s${Math.round(cov.server)}` : '';
    const body = [c, s].filter(Boolean).join('/');
    if (body) parts.push(`cov ${body}%`);
  }
  return parts.join(' · ');
}

/** Detailed multi-line tooltip content for hover. Renders each component
 *  on its own line including the raw version strings. */
export function formatVersionTooltip(meta?: VersionMetaLike): string {
  const lines: string[] = [`Client: ${__APP_VERSION__}`];
  if (meta?.serverVersion) lines.push(`Server: ${meta.serverVersion}`);
  if (meta?.botServiceVersion) lines.push(`Bot Service: ${meta.botServiceVersion}`);
  const cov = meta?.coverage;
  if (cov?.client != null) lines.push(`Coverage (client): ${cov.client.toFixed(1)}%`);
  if (cov?.server != null) lines.push(`Coverage (server): ${cov.server.toFixed(1)}%`);
  return lines.join('\n');
}
